// ============================================================
// pages/HomePage.jsx — Connected to Firebase
// ============================================================

import React, { useState } from "react";
import { useProducts, useReviews } from "../hooks/useFirestore";

const PriceDisplay = ({ product, compact = false }) => {
  const fmt = (p) => `Rs ${p}`;
  if (product.pricingType === "per_size" && product.sizes?.length) {
    if (compact) return <span className="price-tag">From {fmt(Math.min(...product.sizes.map((s) => s.price)))}</span>;
    return <span className="price-tag">{fmt(product.sizes[0]?.price)} — {fmt(product.sizes[product.sizes.length - 1]?.price)}</span>;
  }
  if (product.pricingType === "range") return <span className="price-tag">{fmt(product.rangeMin)} — {fmt(product.rangeMax)}</span>;
  if (product.pricingType === "fixed") {
    if (product.promoPrice) {
      return (
        <span className="price-tag">
          <span style={{ textDecoration: "line-through", opacity: 0.5, marginRight: 6 }}>{fmt(product.fixedPrice)}</span>
          <span style={{ color: "#e74c3c" }}>{fmt(product.promoPrice)}</span>
        </span>
      );
    }
    return <span className="price-tag">{fmt(product.fixedPrice)}</span>;
  }
  return null;
};

export default function HomePage({ setCurrentView, showToast }) {
  const { products } = useProducts();
  const { reviews } = useReviews();
  const [selectedProduct, setSelectedProduct] = useState(null);

  const featured = products.filter((p) => p.featured && p.available);
  const latestReviews = reviews.slice(0, 4);

  return (
    <div>
      {/* Hero */}
      <div className="hero">
        <div className="hero-content">
          <div style={{
            width: 180, height: 180, borderRadius: "50%", overflow: "hidden",
            marginBottom: "1rem", boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
          }}>
            <img src="/logo.jpg" alt="Chaitimecrunch" style={{ width: "108%", height: "108%", objectFit: "cover", marginLeft: "-4%", marginTop: "-4%" }} />
          </div>
          <div className="hero-badge">Artisan Bakery · Beau Bassin</div>
          <h1>Where Every Bite Tells a <em>Story</em></h1>
          <p>
            Handcrafted cakes, cookies & pastries made with love and the finest
            ingredients. From chai-spiced indulgences to classic celebration cakes —
            Chaitimecrunch is your neighbourhood artisan bakery.
          </p>
          <div className="hero-btns">
            <button className="btn btn-primary" onClick={() => setCurrentView("menu")}>
              🧁 Browse Menu
            </button>
            <button className="btn btn-secondary" onClick={() => setCurrentView("book")}>
              📅 Book an Order
            </button>
          </div>
        </div>
      </div>

      {/* Featured Products */}
      <div className="section-header">
        <div>
          <h2>Our Favourites</h2>
          <p>Handpicked bestsellers loved by our community</p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => setCurrentView("menu")}>
          View All →
        </button>
      </div>
      <div className="product-grid">
        {featured.map((p) => (
          <div key={p.id} className="product-card" onClick={() => setSelectedProduct(p)}>
            <div className="product-card-img-wrap">
              <img src={p.thumbnailURL || p.image} alt={p.name} className="product-card-img" />
              {p.promoPrice && <div className="product-card-badge">Sale</div>}
              {p.isNew && <div className="product-card-badge" style={{ background: "var(--sage)" }}>New</div>}
            </div>
            <div className="product-card-body">
              <div className="product-card-cat">{p.category}</div>
              <div className="product-card-name">{p.name}</div>
              <div className="product-card-desc">{p.description}</div>
              <div className="product-card-footer">
                <PriceDisplay product={p} compact />
                <div className="product-card-rating">
                  <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "#f5a623", stroke: "#f5a623" }}>
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  {p.averageRating?.toFixed(1) || p.rating || "—"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Features */}
      <div style={{ marginTop: "4rem" }}>
        <div className="section-header">
          <div>
            <h2>Why Chaitimecrunch?</h2>
            <p>Everything you need, from ordering to loyalty rewards</p>
          </div>
        </div>
        <div className="features-grid">
          {[
            { icon: "📅", title: "Online Booking", desc: "Book, edit, or cancel orders online with real-time availability." },
            { icon: "🔔", title: "Live Notifications", desc: "Get booking confirmations, reminders & delivery updates instantly." },
            { icon: "🎁", title: "Loyalty Rewards", desc: "Earn points with every purchase and unlock exclusive treats." },
            { icon: "🏷️", title: "Exclusive Promos", desc: "Personalised offers and seasonal promotions just for you." },
            { icon: "⭐", title: "Rate & Review", desc: "Share feedback on your experience and help us improve." },
            { icon: "📍", title: "Easy Navigation", desc: "Find us easily with integrated Google Maps directions." },
          ].map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon" style={{ fontSize: "1.5rem" }}>{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Reviews */}
      {latestReviews.length > 0 && (
        <div style={{ marginTop: "4rem" }}>
          <div className="section-header">
            <div>
              <h2>What Our Customers Say</h2>
              <p>Real reviews from real chai-lovers</p>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
            {latestReviews.map((r) => (
              <div key={r.id} className="review-card">
                <div className="review-stars">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <svg key={i} viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "#f5a623", stroke: "#f5a623" }}>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <p style={{ fontSize: "0.9rem", lineHeight: 1.6, marginBottom: "0.75rem" }}>"{r.comment}"</p>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", color: "var(--cocoa)" }}>
                  <strong>{r.customerName}</strong>
                  <span>{r.productName}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProduct(null)}>✕</button>
            <img src={selectedProduct.thumbnailURL || selectedProduct.image} alt={selectedProduct.name} className="modal-img" />
            <div className="modal-body">
              <div className="product-card-cat">{selectedProduct.category}</div>
              <h2>{selectedProduct.name}</h2>
              <p style={{ color: "var(--cocoa)", lineHeight: 1.7, margin: "0.5rem 0 1rem" }}>{selectedProduct.description}</p>

              {selectedProduct.pricingType === "per_size" && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem" }}>Available Sizes</div>
                  <div className="size-picker">
                    {selectedProduct.sizes.map((s) => (
                      <div key={s.label} className="size-btn">
                        {s.label}
                        <span className="size-price">Rs {s.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedProduct.pricingType === "fixed" && (
                <div style={{ fontSize: "1.3rem", fontWeight: 700, margin: "1rem 0" }}>
                  <PriceDisplay product={selectedProduct} />
                </div>
              )}

              {selectedProduct.pricingType === "range" && (
                <div style={{ fontSize: "1.1rem", fontWeight: 600, margin: "1rem 0" }}>
                  Rs {selectedProduct.rangeMin} — Rs {selectedProduct.rangeMax}
                  <div style={{ fontSize: "0.82rem", color: "var(--cocoa)", fontWeight: 400 }}>
                    Final price depends on customisation
                  </div>
                </div>
              )}

              {selectedProduct.preparationTime && (
                <div style={{ fontSize: "0.85rem", color: "var(--cocoa)", marginTop: "0.5rem" }}>
                  ⏱ {selectedProduct.preparationTime}
                </div>
              )}

              {selectedProduct.allergens?.length > 0 && (
                <div style={{ marginTop: "0.75rem" }}>
                  <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 4 }}>Allergens</div>
                  <div className="tag-row">
                    {selectedProduct.allergens.map((a) => (
                      <span key={a} className="inline-tag">{a}</span>
                    ))}
                  </div>
                </div>
              )}

              <button className="btn btn-primary" style={{ marginTop: "1.5rem" }}
                onClick={() => { setSelectedProduct(null); setCurrentView("book"); }}>
                📅 Book This Product
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}